import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUsers, FiFilter } from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const StudentsContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  width: 300px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const FilterButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
  
  &:hover {
    background-color: var(--background-tertiary);
    border-color: var(--primary-color);
  }
  
  &.active {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
  }
`;

const CourseFilter = styled.select`
  padding: 0.75rem 3.5rem 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  min-width: 200px;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.25rem center;
  background-size: 12px;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 150px;
    padding: 0.625rem 2.5rem 0.625rem 0.875rem;
    font-size: 0.8rem;
    min-height: 44px;
    background-position: right 1rem center;
  }

  @media (max-width: 480px) {
    min-width: 100%;
    padding: 0.625rem 2.5rem 0.625rem 0.75rem;
    font-size: 0.75rem;
    min-height: 44px;
    background-position: right 1rem center;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const StudentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StudentCard = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const StudentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-weight: 600;
  font-size: 1.125rem;
  background-image: ${props => props.src ? `url(${props.src})` : 'none'};
  background-size: cover;
  background-position: center;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const StudentInfo = styled.div`
  flex: 1;
`;

const StudentName = styled.h3`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
    
    @media (max-width: 480px) {
      width: 48px;
      height: 48px;
      margin-bottom: 0.75rem;
    }
  }
  
  h3 {
    color: var(--text-primary);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    
    @media (max-width: 480px) {
      font-size: 1.25rem;
    }
  }
  
  p {
    font-size: 1rem;
    margin: 0;
    
    @media (max-width: 480px) {
      font-size: 0.875rem;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
  
  svg {
    width: 32px;
    height: 32px;
    animation: spin 1s linear infinite;
    
    @media (max-width: 480px) {
      width: 24px;
      height: 24px;
    }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

function Students() {
  const { isAuthenticated, user } = useAuth();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('all');

  const fetchStudentsByCourse = useCallback(async (courseId) => {
    try {
      setLoading(true);
      
      // Use different API method based on user role
      const response = (user?.role === 'instructor' || user?.role === 'admin') 
        ? await api.getStudentsByCourse(courseId)
        : await api.getClassmatesByCourse(courseId);
      
      if (response.success) {
        setStudents(response.data || []);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error('Error fetching students by course:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (user?.role === 'instructor' || user?.role === 'admin') {
        // Instructors and admins can see all courses and all students
        const [coursesResponse, studentsResponse] = await Promise.all([
          api.getCoursesForStudents(),
          api.getStudents()
        ]);
        
        if (coursesResponse.success) {
          setCourses(coursesResponse.courses || []);
        }
        
        if (studentsResponse.success) {
          setStudents(studentsResponse.data || []);
        }
      } else {
        // Students can only see courses they're enrolled in and students from those courses
        const coursesResponse = await api.getCourses();
        
        if (coursesResponse.success) {
          const enrolledCourses = coursesResponse.courses || [];
          setCourses(enrolledCourses);
          
          // If there are enrolled courses, get students from the first course by default
          if (enrolledCourses.length > 0) {
            const firstCourseId = enrolledCourses[0].id;
            setSelectedCourse(firstCourseId);
            await fetchStudentsByCourse(firstCourseId);
          } else {
            setStudents([]);
          }
        } else {
          setCourses([]);
          setStudents([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setStudents([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [user, fetchStudentsByCourse]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleCourseChange = (courseId) => {
    setSelectedCourse(courseId);
    if (courseId === 'all') {
      if (user?.role === 'instructor' || user?.role === 'admin') {
        // Only instructors/admins can see all students
        fetchData();
      } else {
        // Students can't see all students, just from their enrolled courses
        setStudents([]);
      }
    } else {
      fetchStudentsByCourse(courseId);
    }
  };

  const formatName = (firstName, lastName) => {
    if (!lastName) return firstName;
    return `${firstName} ${lastName.charAt(0)}`;
  };

  const getInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0) : '';
    const lastInitial = lastName ? lastName.charAt(0) : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
    const formattedName = formatName(student.first_name, student.last_name).toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = 
      fullName.includes(searchLower) || 
      formattedName.includes(searchLower);
    
    return matchesSearch;
  });

  // Check if user has access
  if (!isAuthenticated) {
    return (
      <StudentsContainer>
        <EmptyState>
          <FiUsers />
          <h3>Access Denied</h3>
          <p>Please log in to view students.</p>
        </EmptyState>
      </StudentsContainer>
    );
  }

  if (loading) {
    return (
      <StudentsContainer>
        <LoadingState>
          <FiUsers />
          <p>Loading students...</p>
        </LoadingState>
      </StudentsContainer>
    );
  }

  return (
    <StudentsContainer>
      <Header>
        <Title>Students</Title>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterButton>
            <FiFilter size={16} />
            Filter
          </FilterButton>
        </SearchBar>
      </Header>

      <Filters>
        <CourseFilter 
          value={selectedCourse}
          onChange={(e) => handleCourseChange(e.target.value)}
        >
          {(user?.role === 'instructor' || user?.role === 'admin') && (
            <option value="all">All Courses</option>
          )}
          {courses.map(course => (
            <option key={course.id} value={course.id}>
              {course.code} - {course.name}
            </option>
          ))}
        </CourseFilter>
      </Filters>

      {filteredStudents.length > 0 ? (
        <StudentsGrid>
          {filteredStudents.map((student, index) => (
            <StudentCard
              key={student.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
            >
              <StudentHeader>
                <Avatar>
                  {getInitials(student.first_name, student.last_name)}
                </Avatar>
                <StudentInfo>
                  <StudentName>{formatName(student.first_name, student.last_name)}</StudentName>
                </StudentInfo>
              </StudentHeader>
            </StudentCard>
          ))}
        </StudentsGrid>
      ) : (
        <EmptyState>
          <FiUsers />
          <h3>No students found</h3>
          <p>
            {searchTerm 
              ? `No students match "${searchTerm}"`
              : selectedCourse === 'all' 
                ? (user?.role === 'instructor' || user?.role === 'admin')
                  ? 'No students are enrolled in any courses.'
                  : 'No students found in your enrolled courses.'
                : 'No students are enrolled in the selected course.'
            }
          </p>
        </EmptyState>
      )}
    </StudentsContainer>
  );
}

export default Students;
